import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "../services/api";
import { useAuth } from "../context/AuthContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const ViewProfileLayer = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();

  // Extract userId from location state OR from URL search parameters
  const getUserId = () => {
    // Check if userId is in location state
    if (location.state?.userId) {
      return location.state.userId;
    }

    // Check if userId is in URL search parameters
    const searchParams = new URLSearchParams(location.search);
    const idFromUrl = searchParams.get("id");
    if (idFromUrl) {
      return idFromUrl;
    }

    // If no userId is provided, return null (will use current user)
    return null;
  };

  const userId = getUserId();

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
  const [passwordChangeLoading, setPasswordChangeLoading] = useState(false);
  const [blockLoading, setBlockLoading] = useState(false);
  const [examResults, setExamResults] = useState([]);
  const [examLoading, setExamLoading] = useState(false);

  // Form states
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
  });

  // Password states
  const [passwords, setPasswords] = useState({
    newPassword: "",
    confirmPassword: "",
  });

  // Subscription modal states
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState("");
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [plans, setPlans] = useState([]);

  // Determine if viewing own profile
  const isOwnProfile = !userId || userId === currentUser?.id;
  const isAdminChangingPassword =
    !isOwnProfile && currentUser?.role === "ADMIN";
  // Determine if current user can block/unblock the viewed user
  const canToggleBlockStatus =
    !isOwnProfile && currentUser?.role === "ADMIN" && user?.role !== "ADMIN";

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // Get subscription status badge class
  const getSubscriptionBadgeClass = (subscription) => {
    if (!subscription || !subscription.isActive) {
      return "bg-neutral-200 text-neutral-700";
    }

    const planName = subscription.plan?.name?.toUpperCase();

    switch (planName) {
      case "FREE":
        return "bg-info-100 text-info-700";
      case "BASIC":
        return "bg-success-100 text-success-700";
      case "PRO":
        return "bg-primary-100 text-primary-700";
      case "ENTERPRISE":
        return "bg-warning-100 text-warning-700";
      default:
        return "bg-neutral-200 text-neutral-700";
    }
  };

  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        setLoading(true);
        let response;

        if (userId) {
          // Fetch specific user if userId is provided
          response = await api.users.getById(userId);
        } else if (currentUser) {
          // Use current logged-in user if no userId is provided
          response = { success: true, data: currentUser };

          // Or alternatively fetch fresh data for current user
          // response = await api.auth.getCurrentUser();
        } else {
          // No userId and no current user
          setError("User not authenticated");
          setLoading(false);
          return;
        }

        if (response.success) {
          setUser(response.data);
          // Update form data with user information
          setFormData({
            firstName: response.data.firstName || "",
            lastName: response.data.lastName || "",
            email: response.data.email || "",
            phone: response.data.phone || "",
          });

          // Set avatar if available
          if (response.data.avatar) {
            setImagePreview(response.data.avatar);
          }

          // Fetch user exam results
          fetchUserExamResults(response.data.id);
        } else {
          setError(response.message || "Failed to fetch user details");
        }
      } catch (err) {
        setError("Error connecting to server. Please try again later.");
        console.error("Error fetching user details:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchUserDetails();
  }, [userId, currentUser]);

  // Fetch user exam results
  const fetchUserExamResults = async (userId) => {
    try {
      setExamLoading(true);
      const response = await api.exams.getUserResults(userId);

      if (response.success) {
        setExamResults(response.data);
      } else {
        console.error("Failed to fetch exam results:", response.message);
      }
    } catch (err) {
      console.error("Error fetching exam results:", err);
    } finally {
      setExamLoading(false);
    }
  };

  // Helper functions for exam data
  const getExamName = (result) => {
    if (result.exam && result.exam.title) {
      return result.exam.title;
    }
    return result.examName || result.title || "Unknown Exam";
  };

  const getExamScore = (result) => {
    return result.score || result.scorePercentage || 0;
  };

  const wasExamPassed = (result) => {
    return result.passed || result.isPassed || false;
  };

  // Calculate exam statistics
  const getExamStats = () => {
    if (!examResults || examResults.length === 0) {
      return {
        totalExams: 0,
        passedExams: 0,
        passRate: 0,
        averageScore: 0,
      };
    }

    const totalExams = examResults.length;
    const passedExams = examResults.filter((result) =>
      wasExamPassed(result)
    ).length;
    const passRate = Math.round((passedExams / totalExams) * 100);
    const totalScore = examResults.reduce(
      (sum, result) => sum + getExamScore(result),
      0
    );
    const averageScore = Math.round(totalScore / totalExams);

    return {
      totalExams,
      passedExams,
      passRate,
      averageScore,
    };
  };

  // Get exam statistics
  const examStats = getExamStats();

  // Toggle functions for password fields
  const togglePasswordVisibility = () => {
    setPasswordVisible(!passwordVisible);
  };

  const toggleConfirmPasswordVisibility = () => {
    setConfirmPasswordVisible(!confirmPasswordVisible);
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  // Handle password input changes
  const handlePasswordChange = (e) => {
    const { id, value } = e.target;
    setPasswords((prev) => ({
      ...prev,
      [id === "your-password" ? "newPassword" : "confirmPassword"]: value,
    }));
  };

  // Handle profile update
  const handleUpdateProfile = async () => {
    try {
      const updatedUserData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone,
        // Other fields that API supports
      };

      let response;
      if (userId && userId !== currentUser?.id) {
        // Admin updating another user
        response = await api.users.update(userId, updatedUserData);
      } else {
        // User updating their own profile
        response = await api.users.update(currentUser.id, updatedUserData);
      }

      if (response.success) {
        toast.success("Profile updated successfully");
        setUser(response.data);
      } else {
        toast.error(response.message || "Failed to update profile");
      }
    } catch (err) {
      toast.error("Error updating profile. Please try again.");
      console.error("Error updating profile:", err);
    }
  };

  // Handle password update
  const handleUpdatePassword = async () => {
    if (passwords.newPassword !== passwords.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    if (passwords.newPassword.length < 6) {
      toast.error("Password must be at least 6 characters long");
      return;
    }

    setPasswordChangeLoading(true);

    try {
      let response;

      if (isAdminChangingPassword) {
        // Admin changing another user's password
        response = await api.users.changePassword(
          userId,
          passwords.newPassword
        );

        if (response.success) {
          toast.success(
            `Password for ${user.firstName} ${user.lastName} changed successfully`
          );
          setPasswords({
            newPassword: "",
            confirmPassword: "",
          });
        } else {
          toast.error(response.message || "Failed to change password");
        }
      } else {
        // User changing their own password through profile page API
        // Note: You would need to implement this endpoint if it doesn't exist yet
        // This would typically require current password verification
        toast.info("This feature is not implemented for own profile yet");

        // If you want to implement it later, you would call an API like this:
        // response = await api.auth.updatePassword({
        //   currentPassword: "...",  // You would need to collect this
        //   newPassword: passwords.newPassword
        // });
      }
    } catch (err) {
      toast.error("Error changing password. Please try again.");
      console.error("Error changing password:", err);
    } finally {
      setPasswordChangeLoading(false);
    }
  };

  // Handle block/unblock user
  const handleToggleBlockStatus = async () => {
    if (!user || !canToggleBlockStatus) return;

    setBlockLoading(true);
    try {
      const response = await api.users.toggleUserBlock(
        user.id,
        !user.isBlocked
      );

      if (response.success) {
        toast.success(
          response.message ||
            `User has been ${response.data.isBlocked ? "blocked" : "unblocked"}`
        );
        setUser(response.data);
      } else {
        toast.error(response.message || "Failed to update user block status");
      }
    } catch (err) {
      toast.error("Error updating user block status. Please try again.");
      console.error("Error toggling user block status:", err);
    } finally {
      setBlockLoading(false);
    }
  };

  const readURL = (input) => {
    if (input.target.files && input.target.files[0]) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(input.target.files[0]);
    }
  };

  // Fetch subscription plans
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await api.plans.getAll();
        if (response.success) {
          setPlans(response.data);
        } else {
          console.error("Failed to fetch plans:", response.message);
        }
      } catch (error) {
        console.error("Error fetching plans:", error);
      }
    };

    fetchPlans();
  }, []);

  // Handle buying subscription for user
  const handleBuySubscription = () => {
    setSelectedPlan("");
    setShowSubscriptionModal(true);
  };

  // Handle subscription purchase
  const handleSubscriptionPurchase = async () => {
    if (!selectedPlan) {
      alert("Please select a subscription plan");
      return;
    }

    setSubscriptionLoading(true);

    try {
      // Get the selected plan details
      const planDetails = plans.find((plan) => plan.id === selectedPlan);

      // Calculate start and end dates
      const startDate = new Date();
      const endDate = new Date(startDate);

      // Calculate end date based on plan duration
      if (planDetails.durationType === "WEEK") {
        endDate.setDate(endDate.getDate() + planDetails.duration * 7);
      } else {
        // Default to MONTH
        endDate.setMonth(endDate.getMonth() + planDetails.duration);
      }

      // Create subscription data
      const subscriptionData = {
        userId: user.id,
        planId: selectedPlan,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        isActive: true,
        autoRenew: false,
      };

      const response = await api.subscriptions.createSubscription(
        subscriptionData
      );

      if (response.success) {
        // Update user in the state with new subscription data
        setUser({
          ...user,
          subscription: response.data,
        });

        // Hide modal and reset states
        setShowSubscriptionModal(false);
        setSelectedPlan("");

        alert("Subscription purchased successfully");
      } else {
        alert(response.message || "Failed to purchase subscription");
      }
    } catch (error) {
      console.error("Error purchasing subscription:", error);
      alert("An error occurred while purchasing the subscription");
    } finally {
      setSubscriptionLoading(false);
    }
  };

  // Find selected plan details
  const getSelectedPlanDetails = () => {
    if (!selectedPlan) return null;
    return plans.find((plan) => plan.id === selectedPlan);
  };

  const selectedPlanDetails = getSelectedPlanDetails();

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2">Loading user profile...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger text-center" role="alert">
        {error}
        <div className="mt-3">
          <button className="btn btn-primary" onClick={() => navigate(-1)}>
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="alert alert-warning text-center" role="alert">
        User not found
        <div className="mt-3">
          <button
            className="btn btn-primary"
            onClick={() => navigate("/users-grid")}
          >
            Go to Users List
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="row gy-4">
        <ToastContainer />
        <div className="col-lg-4">
          <div className="user-grid-card position-relative border radius-16 overflow-hidden bg-base h-100">
            <div className="bg-light p-3">
              {/* Empty div for consistent spacing */}
            </div>
            <div className="pb-24 ms-16 mb-24 me-16 mt-3">
              <div className="text-center border border-top-0 border-start-0 border-end-0 pb-4">
                {user.avatar ? (
                  <img
                    src={user.avatar}
                    alt={user.name}
                    className="border border-width-2-px w-200-px h-200-px rounded-circle object-fit-cover"
                  />
                ) : (
                  <div
                    className="border w-200-px h-200-px rounded-circle d-flex justify-content-center align-items-center bg-primary-600 text-white mx-auto"
                    style={{ fontSize: "4rem" }}
                  >
                    {(user.name || user.firstName || user.email || "")
                      .charAt(0)
                      .toUpperCase()}
                  </div>
                )}
                <h6 className="mb-0 mt-16">
                  {user.name || `${user.firstName} ${user.lastName}`}
                </h6>
                <span className="text-secondary-light mb-16">{user.email}</span>
                <div className="d-flex justify-content-center gap-2 mb-3">
                  {isOwnProfile && (
                    <div className="badge bg-success">My Profile</div>
                  )}
                  {user.isBlocked && (
                    <div className="badge bg-danger">Blocked</div>
                  )}
                </div>

                {canToggleBlockStatus && (
                  <div className="mt-3 mb-3">
                    <button
                      className={`btn ${
                        user.isBlocked ? "btn-success" : "btn-danger"
                      }`}
                      onClick={handleToggleBlockStatus}
                      disabled={blockLoading}
                    >
                      {blockLoading ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                            aria-hidden="true"
                          ></span>
                          Processing...
                        </>
                      ) : (
                        <>
                          <Icon
                            icon={user.isBlocked ? "mdi:lock-open" : "mdi:lock"}
                            className="me-1"
                          />
                          {user.isBlocked ? "Unblock User" : "Block User"}
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
              <div className="mt-24">
                <h6 className="text-xl mb-16">Personal Info</h6>
                <ul>
                  <li className="d-flex align-items-center gap-1 mb-12">
                    <span className="w-30 text-md fw-semibold text-primary-light">
                      Full Name
                    </span>
                    <span className="w-70 text-secondary-light fw-medium">
                      : {user.firstName} {user.lastName}
                    </span>
                  </li>
                  <li className="d-flex align-items-center gap-1 mb-12">
                    <span className="w-30 text-md fw-semibold text-primary-light">
                      Email
                    </span>
                    <span className="w-70 text-secondary-light fw-medium">
                      : {user.email}
                    </span>
                  </li>
                  <li className="d-flex align-items-center gap-1 mb-12">
                    <span className="w-30 text-md fw-semibold text-primary-light">
                      Phone Number
                    </span>
                    <span className="w-70 text-secondary-light fw-medium">
                      : {user.phone || "Not specified"}
                    </span>
                  </li>
                  <li className="d-flex align-items-center gap-1 mb-12">
                    <span className="w-30 text-md fw-semibold text-primary-light">
                      Role
                    </span>
                    <span className="w-70 text-secondary-light fw-medium">
                      : {user.role}
                    </span>
                  </li>
                  <li className="d-flex align-items-center gap-1 mb-12">
                    <span className="w-30 text-md fw-semibold text-primary-light">
                      Status
                    </span>
                    <span
                      className={`w-70 fw-medium ${
                        user.isBlocked ? "text-danger" : "text-success"
                      }`}
                    >
                      : {user.isBlocked ? "Blocked" : "Active"}
                    </span>
                  </li>
                  <li className="d-flex align-items-center gap-1 mb-12">
                    <span className="w-30 text-md fw-semibold text-primary-light">
                      Joined
                    </span>
                    <span className="w-70 text-secondary-light fw-medium">
                      : {new Date(user.createdAt).toLocaleDateString()}
                    </span>
                  </li>
                  <li className="d-flex align-items-center gap-1 mb-12">
                    <span className="w-30 text-md fw-semibold text-primary-light">
                      Last Updated
                    </span>
                    <span className="w-70 text-secondary-light fw-medium">
                      : {new Date(user.updatedAt).toLocaleDateString()}
                    </span>
                  </li>
                  {user.bio && (
                    <li className="d-flex align-items-center gap-1">
                      <span className="w-30 text-md fw-semibold text-primary-light">
                        Bio
                      </span>
                      <span className="w-70 text-secondary-light fw-medium">
                        : {user.bio}
                      </span>
                    </li>
                  )}
                </ul>
              </div>

              {/* Subscription Information */}
              <div className="mt-24">
                <h6 className="text-xl mb-16">Subscription Info</h6>
                {user.subscription ? (
                  <div className="border rounded p-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h6 className="mb-0">Plan:</h6>
                      <span
                        className={`badge ${getSubscriptionBadgeClass(
                          user.subscription
                        )} px-3 py-2`}
                      >
                        {user.subscription.plan?.displayName ||
                          user.subscription.plan?.name ||
                          "Unknown"}
                      </span>
                    </div>

                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span>Status:</span>
                      <span
                        className={`badge ${
                          user.subscription.isActive
                            ? "bg-success"
                            : "bg-danger"
                        }`}
                      >
                        {user.subscription.isActive ? "Active" : "Inactive"}
                      </span>
                    </div>

                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span>Start Date:</span>
                      <span>{formatDate(user.subscription.startDate)}</span>
                    </div>

                    {user.subscription.endDate && (
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span>End Date:</span>
                        <span>{formatDate(user.subscription.endDate)}</span>
                      </div>
                    )}

                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span>Auto Renew:</span>
                      <span
                        className={`badge ${
                          user.subscription.autoRenew
                            ? "bg-success"
                            : "bg-secondary"
                        }`}
                      >
                        {user.subscription.autoRenew ? "Yes" : "No"}
                      </span>
                    </div>

                    {user.subscription.lastPaymentDate && (
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span>Last Payment:</span>
                        <span>
                          {formatDate(user.subscription.lastPaymentDate)}
                        </span>
                      </div>
                    )}

                    {user.subscription.nextPaymentDate && (
                      <div className="d-flex justify-content-between align-items-center">
                        <span>Next Payment:</span>
                        <span>
                          {formatDate(user.subscription.nextPaymentDate)}
                        </span>
                      </div>
                    )}

                    <div className="text-center mt-4">
                      <button
                        className="btn btn-success btn-sm"
                        onClick={handleBuySubscription}
                      >
                        <Icon icon="mdi:credit-card-outline" className="me-1" />{" "}
                        Buy New Subscription
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center border rounded p-3">
                    <p className="text-muted mb-3">
                      No subscription data available
                    </p>
                    <button
                      className="btn btn-success btn-sm"
                      onClick={handleBuySubscription}
                    >
                      <Icon icon="mdi:credit-card-outline" className="me-1" />{" "}
                      Buy Subscription
                    </button>
                  </div>
                )}
              </div>

              {/* Exam Statistics */}
              <div className="mt-24">
                <h6 className="text-xl mb-16">Exam Statistics</h6>
                {examLoading ? (
                  <div className="text-center py-3">
                    <div
                      className="spinner-border spinner-border-sm text-primary"
                      role="status"
                    >
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2 mb-0">Loading exam data...</p>
                  </div>
                ) : examResults.length === 0 ? (
                  <div className="text-center border rounded p-3">
                    <p className="mb-0 text-muted">No exam data available</p>
                  </div>
                ) : (
                  <div className="border rounded p-3">
                    <div className="row mb-3">
                      <div className="col-6">
                        <div className="d-flex flex-column align-items-center p-2 border rounded bg-light">
                          <h3 className="mb-0 text-primary">
                            {examStats.totalExams}
                          </h3>
                          <span className="text-muted small">Total Exams</span>
                        </div>
                      </div>
                      <div className="col-6">
                        <div className="d-flex flex-column align-items-center p-2 border rounded bg-light">
                          <h3 className="mb-0 text-success">
                            {examStats.passedExams}
                          </h3>
                          <span className="text-muted small">Passed</span>
                        </div>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-6">
                        <div className="d-flex flex-column align-items-center p-2 border rounded bg-light">
                          <h3 className="mb-0 text-info">
                            {examStats.passRate}%
                          </h3>
                          <span className="text-muted small">Pass Rate</span>
                        </div>
                      </div>
                      <div className="col-6">
                        <div className="d-flex flex-column align-items-center p-2 border rounded bg-light">
                          <h3 className="mb-0 text-warning">
                            {examStats.averageScore}%
                          </h3>
                          <span className="text-muted small">Avg. Score</span>
                        </div>
                      </div>
                    </div>

                    <h6 className="mt-4 mb-2">Recent Exams</h6>
                    <div className="table-responsive">
                      <table className="table table-sm table-bordered">
                        <thead className="bg-light">
                          <tr>
                            <th>Exam</th>
                            <th>Date</th>
                            <th>Score</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {examResults.slice(0, 5).map((result) => (
                            <tr key={result.id}>
                              <td
                                className="text-nowrap overflow-hidden"
                                style={{
                                  maxWidth: "120px",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {getExamName(result)}
                              </td>
                              <td>
                                {formatDate(
                                  result.completedAt || result.createdAt
                                )}
                              </td>
                              <td>{getExamScore(result)}%</td>
                              <td>
                                <span
                                  className={`badge ${
                                    wasExamPassed(result)
                                      ? "bg-success"
                                      : "bg-danger"
                                  }`}
                                >
                                  {wasExamPassed(result) ? "Passed" : "Failed"}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {examResults.length > 5 && (
                      <div className="text-center mt-2">
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => navigate(`/exams/student/${user.id}`)}
                        >
                          View All Exams
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Rest of the component remains unchanged */}
        <div className="col-lg-8">
          <div className="card h-100">
            <div className="card-body p-24">
              <ul
                className="nav border-gradient-tab nav-pills mb-20 d-inline-flex"
                id="pills-tab"
                role="tablist"
              >
                <li className="nav-item" role="presentation">
                  <button
                    className="nav-link d-flex align-items-center px-24 active"
                    id="pills-edit-profile-tab"
                    data-bs-toggle="pill"
                    data-bs-target="#pills-edit-profile"
                    type="button"
                    role="tab"
                    aria-controls="pills-edit-profile"
                    aria-selected="true"
                  >
                    Edit Profile
                  </button>
                </li>
                <li className="nav-item" role="presentation">
                  <button
                    className="nav-link d-flex align-items-center px-24"
                    id="pills-change-passwork-tab"
                    data-bs-toggle="pill"
                    data-bs-target="#pills-change-passwork"
                    type="button"
                    role="tab"
                    aria-controls="pills-change-passwork"
                    aria-selected="false"
                    tabIndex={-1}
                  >
                    Change Password
                  </button>
                </li>
                <li className="nav-item" role="presentation">
                  <button
                    className="nav-link d-flex align-items-center px-24"
                    id="pills-notification-tab"
                    data-bs-toggle="pill"
                    data-bs-target="#pills-notification"
                    type="button"
                    role="tab"
                    aria-controls="pills-notification"
                    aria-selected="false"
                    tabIndex={-1}
                  >
                    Notification Settings
                  </button>
                </li>
                <li className="nav-item" role="presentation">
                  <button
                    className="nav-link d-flex align-items-center px-24"
                    id="pills-subscription-tab"
                    data-bs-toggle="pill"
                    data-bs-target="#pills-subscription"
                    type="button"
                    role="tab"
                    aria-controls="pills-subscription"
                    aria-selected="false"
                    tabIndex={-1}
                  >
                    Subscription Details
                  </button>
                </li>
              </ul>
              <div className="tab-content" id="pills-tabContent">
                <div
                  className="tab-pane fade show active"
                  id="pills-edit-profile"
                  role="tabpanel"
                  aria-labelledby="pills-edit-profile-tab"
                  tabIndex={0}
                >
                  <h6 className="text-md text-primary-light mb-16">
                    Profile Image
                  </h6>
                  {/* Upload Image Start */}
                  <div className="mb-24 mt-16">
                    <div className="avatar-upload">
                      <div className="avatar-edit position-absolute bottom-0 end-0 me-24 mt-16 z-1 cursor-pointer">
                        <input
                          type="file"
                          id="imageUpload"
                          accept=".png, .jpg, .jpeg"
                          hidden
                          onChange={readURL}
                        />
                        <label
                          htmlFor="imageUpload"
                          className="w-32-px h-32-px d-flex justify-content-center align-items-center bg-primary-50 text-primary-600 border border-primary-600 bg-hover-primary-100 text-lg rounded-circle"
                        >
                          <Icon
                            icon="solar:camera-outline"
                            className="icon"
                          ></Icon>
                        </label>
                      </div>
                      <div className="avatar-preview">
                        {imagePreview ? (
                          <div
                            id="imagePreview"
                            style={{
                              backgroundImage: `url(${imagePreview})`,
                              backgroundSize: "cover",
                              backgroundPosition: "center",
                            }}
                          />
                        ) : (
                          <div
                            className="d-flex justify-content-center align-items-center bg-primary-600 text-white h-100 w-100"
                            style={{ fontSize: "2.5rem" }}
                          >
                            {(
                              user?.name ||
                              user?.firstName ||
                              user?.email ||
                              ""
                            )
                              .charAt(0)
                              .toUpperCase()}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  {/* Upload Image End */}
                  <form action="#">
                    <div className="row">
                      <div className="col-sm-6">
                        <div className="mb-20">
                          <label
                            htmlFor="firstName"
                            className="form-label fw-semibold text-primary-light text-sm mb-8"
                          >
                            First Name
                            <span className="text-danger-600">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control radius-8"
                            id="firstName"
                            placeholder="First Name"
                            value={formData.firstName}
                            onChange={handleInputChange}
                          />
                        </div>
                      </div>
                      <div className="col-sm-6">
                        <div className="mb-20">
                          <label
                            htmlFor="lastName"
                            className="form-label fw-semibold text-primary-light text-sm mb-8"
                          >
                            Last Name
                            <span className="text-danger-600">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control radius-8"
                            id="lastName"
                            placeholder="Last Name"
                            value={formData.lastName}
                            onChange={handleInputChange}
                          />
                        </div>
                      </div>
                      <div className="col-sm-6">
                        <div className="mb-20">
                          <label
                            htmlFor="email"
                            className="form-label fw-semibold text-primary-light text-sm mb-8"
                          >
                            Email <span className="text-danger-600">*</span>
                          </label>
                          <input
                            type="email"
                            className="form-control radius-8"
                            id="email"
                            placeholder="Enter email address"
                            value={formData.email}
                            onChange={handleInputChange}
                            readOnly
                          />
                        </div>
                      </div>
                      <div className="col-sm-6">
                        <div className="mb-20">
                          <label
                            htmlFor="phone"
                            className="form-label fw-semibold text-primary-light text-sm mb-8"
                          >
                            Phone
                          </label>
                          <input
                            type="tel"
                            className="form-control radius-8"
                            id="phone"
                            placeholder="Enter phone number"
                            value={formData.phone}
                            onChange={handleInputChange}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="d-flex align-items-center justify-content-center gap-3">
                      <button
                        type="button"
                        className="border border-danger-600 bg-hover-danger-200 text-danger-600 text-md px-56 py-11 radius-8"
                        onClick={() => navigate(-1)}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="btn btn-primary border border-primary-600 text-md px-56 py-12 radius-8"
                        onClick={handleUpdateProfile}
                      >
                        Save
                      </button>
                    </div>
                  </form>
                </div>
                <div
                  className="tab-pane fade"
                  id="pills-change-passwork"
                  role="tabpanel"
                  aria-labelledby="pills-change-passwork-tab"
                  tabIndex="0"
                >
                  {isAdminChangingPassword && (
                    <div className="alert alert-info mb-3">
                      <div className="d-flex align-items-center">
                        <Icon
                          icon="mdi:information-outline"
                          className="me-2 text-xl"
                        />
                        <div>
                          You are changing the password for {user?.firstName}{" "}
                          {user?.lastName} as an administrator.
                          <br />
                          The user will be notified via email of this change.
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="mb-20">
                    <label
                      htmlFor="your-password"
                      className="form-label fw-semibold text-primary-light text-sm mb-8"
                    >
                      New Password <span className="text-danger-600">*</span>
                    </label>
                    <div className="position-relative">
                      <input
                        type={passwordVisible ? "text" : "password"}
                        className="form-control radius-8"
                        id="your-password"
                        placeholder="Enter New Password*"
                        value={passwords.newPassword}
                        onChange={handlePasswordChange}
                      />
                      <span
                        className={`toggle-password ${
                          passwordVisible ? "ri-eye-off-line" : "ri-eye-line"
                        } cursor-pointer position-absolute end-0 top-50 translate-middle-y me-16 text-secondary-light`}
                        onClick={togglePasswordVisibility}
                      ></span>
                    </div>
                  </div>

                  <div className="mb-20">
                    <label
                      htmlFor="confirm-password"
                      className="form-label fw-semibold text-primary-light text-sm mb-8"
                    >
                      Confirm Password{" "}
                      <span className="text-danger-600">*</span>
                    </label>
                    <div className="position-relative">
                      <input
                        type={confirmPasswordVisible ? "text" : "password"}
                        className="form-control radius-8"
                        id="confirm-password"
                        placeholder="Confirm Password*"
                        value={passwords.confirmPassword}
                        onChange={handlePasswordChange}
                      />
                      <span
                        className={`toggle-password ${
                          confirmPasswordVisible
                            ? "ri-eye-off-line"
                            : "ri-eye-line"
                        } cursor-pointer position-absolute end-0 top-50 translate-middle-y me-16 text-secondary-light`}
                        onClick={toggleConfirmPasswordVisibility}
                      ></span>
                    </div>
                  </div>

                  <div className="d-flex align-items-center justify-content-center gap-3 mt-4">
                    <button
                      type="button"
                      className="btn btn-primary border border-primary-600 text-md px-56 py-12 radius-8"
                      onClick={handleUpdatePassword}
                      disabled={passwordChangeLoading}
                    >
                      {passwordChangeLoading ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                            aria-hidden="true"
                          ></span>
                          Processing...
                        </>
                      ) : isAdminChangingPassword ? (
                        "Change User Password"
                      ) : (
                        "Update Password"
                      )}
                    </button>
                  </div>
                </div>
                <div
                  className="tab-pane fade"
                  id="pills-notification"
                  role="tabpanel"
                  aria-labelledby="pills-notification-tab"
                  tabIndex={0}
                >
                  <div className="form-switch switch-primary py-12 px-16 border radius-8 position-relative mb-16">
                    <label
                      htmlFor="companzNew"
                      className="position-absolute w-100 h-100 start-0 top-0"
                    />
                    <div className="d-flex align-items-center gap-3 justify-content-between">
                      <span className="form-check-label line-height-1 fw-medium text-secondary-light">
                        Company News
                      </span>
                      <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        id="companzNew"
                      />
                    </div>
                  </div>
                  <div className="form-switch switch-primary py-12 px-16 border radius-8 position-relative mb-16">
                    <label
                      htmlFor="pushNotifcation"
                      className="position-absolute w-100 h-100 start-0 top-0"
                    />
                    <div className="d-flex align-items-center gap-3 justify-content-between">
                      <span className="form-check-label line-height-1 fw-medium text-secondary-light">
                        Push Notification
                      </span>
                      <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        id="pushNotifcation"
                        defaultChecked=""
                      />
                    </div>
                  </div>
                  <div className="form-switch switch-primary py-12 px-16 border radius-8 position-relative mb-16">
                    <label
                      htmlFor="weeklyLetters"
                      className="position-absolute w-100 h-100 start-0 top-0"
                    />
                    <div className="d-flex align-items-center gap-3 justify-content-between">
                      <span className="form-check-label line-height-1 fw-medium text-secondary-light">
                        Weekly News Letters
                      </span>
                      <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        id="weeklyLetters"
                        defaultChecked=""
                      />
                    </div>
                  </div>
                  <div className="form-switch switch-primary py-12 px-16 border radius-8 position-relative mb-16">
                    <label
                      htmlFor="meetUp"
                      className="position-absolute w-100 h-100 start-0 top-0"
                    />
                    <div className="d-flex align-items-center gap-3 justify-content-between">
                      <span className="form-check-label line-height-1 fw-medium text-secondary-light">
                        Meetups Near you
                      </span>
                      <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        id="meetUp"
                      />
                    </div>
                  </div>
                  <div className="form-switch switch-primary py-12 px-16 border radius-8 position-relative mb-16">
                    <label
                      htmlFor="orderNotification"
                      className="position-absolute w-100 h-100 start-0 top-0"
                    />
                    <div className="d-flex align-items-center gap-3 justify-content-between">
                      <span className="form-check-label line-height-1 fw-medium text-secondary-light">
                        Orders Notifications
                      </span>
                      <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        id="orderNotification"
                        defaultChecked=""
                      />
                    </div>
                  </div>
                </div>
                <div
                  className="tab-pane fade"
                  id="pills-subscription"
                  role="tabpanel"
                  aria-labelledby="pills-subscription-tab"
                  tabIndex={0}
                >
                  <h6 className="text-xl mb-16">Subscription Information</h6>

                  {user.subscription ? (
                    <div className="border rounded p-3">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span>Plan Name:</span>
                        <span
                          className={`badge ${getSubscriptionBadgeClass(
                            user.subscription
                          )} px-3 py-2`}
                        >
                          {user.subscription.plan?.displayName ||
                            user.subscription.plan?.name}
                        </span>
                      </div>

                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span>Status:</span>
                        <span
                          className={`badge ${
                            user.subscription.isActive
                              ? "bg-success"
                              : "bg-danger"
                          }`}
                        >
                          {user.subscription.isActive ? "Active" : "Inactive"}
                        </span>
                      </div>

                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span>Start Date:</span>
                        <span>{formatDate(user.subscription.startDate)}</span>
                      </div>

                      {user.subscription.endDate && (
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <span>End Date:</span>
                          <span>{formatDate(user.subscription.endDate)}</span>
                        </div>
                      )}

                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span>Auto Renew:</span>
                        <span
                          className={`badge ${
                            user.subscription.autoRenew
                              ? "bg-success"
                              : "bg-secondary"
                          }`}
                        >
                          {user.subscription.autoRenew ? "Enabled" : "Disabled"}
                        </span>
                      </div>

                      {user.subscription.lastPaymentDate && (
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <span>Last Payment:</span>
                          <span>
                            {formatDate(user.subscription.lastPaymentDate)}
                          </span>
                        </div>
                      )}

                      {user.subscription.nextPaymentDate && (
                        <div className="d-flex justify-content-between align-items-center">
                          <span>Next Payment:</span>
                          <span>
                            {formatDate(user.subscription.nextPaymentDate)}
                          </span>
                        </div>
                      )}

                      <div className="text-center mt-4">
                        <button
                          className="btn btn-success btn-sm"
                          onClick={handleBuySubscription}
                        >
                          <Icon
                            icon="mdi:credit-card-outline"
                            className="me-1"
                          />{" "}
                          Buy New Subscription
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center border rounded p-3">
                      <h6 className="mb-1">No Subscription</h6>
                      <p className="text-muted mb-3">
                        This user doesn't have an active subscription plan.
                      </p>
                      <button
                        className="btn btn-success btn-sm"
                        onClick={handleBuySubscription}
                      >
                        <Icon icon="mdi:credit-card-outline" className="me-1" />{" "}
                        Buy Subscription
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Subscription Purchase Modal */}
      <SubscriptionPurchaseModal
        show={showSubscriptionModal}
        onClose={() => setShowSubscriptionModal(false)}
        user={user || {}}
        plans={plans}
        selectedPlan={selectedPlan}
        setSelectedPlan={setSelectedPlan}
        onSubmit={handleSubscriptionPurchase}
        loading={subscriptionLoading}
        selectedPlanDetails={selectedPlanDetails}
      />
    </>
  );
};

// Subscription Purchase Modal
const SubscriptionPurchaseModal = ({
  show,
  onClose,
  user,
  plans,
  selectedPlan,
  setSelectedPlan,
  onSubmit,
  loading,
  selectedPlanDetails,
}) => {
  if (!show) return null;

  return (
    <>
      {/* Modal backdrop */}
      <div
        className="modal-backdrop fade show"
        onClick={!loading ? onClose : undefined}
        style={{ zIndex: 1050 }}
      ></div>

      {/* Modal dialog */}
      <div
        className="modal fade show"
        style={{ display: "block", zIndex: 1051 }}
        tabIndex="-1"
        role="dialog"
      >
        <div className="modal-dialog modal-dialog-centered" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                Buy Subscription for {user.firstName} {user.lastName}
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={onClose}
                disabled={loading}
              ></button>
            </div>
            <div className="modal-body">
              <div className="mb-3">
                <label className="form-label">Select Subscription Plan</label>
                <select
                  className="form-select"
                  value={selectedPlan}
                  onChange={(e) => setSelectedPlan(e.target.value)}
                  disabled={loading}
                >
                  <option value="">Select a plan</option>
                  {plans.map((plan) => (
                    <option key={plan.id} value={plan.id}>
                      {plan.displayName || plan.name} (${plan.price} /{" "}
                      {plan.duration} {plan.durationType.toLowerCase()}
                      {plan.duration > 1 ? "s" : ""})
                    </option>
                  ))}
                </select>
              </div>

              {selectedPlanDetails && (
                <div className="alert alert-info">
                  <h6>Plan Details:</h6>
                  <p>
                    <strong>Name:</strong>{" "}
                    {selectedPlanDetails.displayName ||
                      selectedPlanDetails.name}
                  </p>
                  <p>
                    <strong>Price:</strong> ${selectedPlanDetails.price} per{" "}
                    {selectedPlanDetails.duration}{" "}
                    {selectedPlanDetails.durationType.toLowerCase()}
                    {selectedPlanDetails.duration > 1 ? "s" : ""}
                  </p>
                  {selectedPlanDetails.description && (
                    <p>
                      <strong>Description:</strong>{" "}
                      {selectedPlanDetails.description}
                    </p>
                  )}
                  {selectedPlanDetails.features &&
                    selectedPlanDetails.features.length > 0 && (
                      <>
                        <p>
                          <strong>Features:</strong>
                        </p>
                        <ul className="mb-0">
                          {selectedPlanDetails.features.map(
                            (feature, index) => (
                              <li key={index}>{feature}</li>
                            )
                          )}
                        </ul>
                      </>
                    )}
                </div>
              )}

              {user.subscription && (
                <div className="alert alert-warning">
                  <h6>Current Subscription:</h6>
                  <p>
                    This user already has a{" "}
                    {user.subscription.isActive ? "active" : "inactive"}{" "}
                    {user.subscription.plan?.displayName ||
                      user.subscription.plan?.name ||
                      "Unknown"}{" "}
                    plan.
                  </p>
                  <p>
                    Purchasing a new subscription will replace their current
                    one.
                  </p>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-success"
                onClick={onSubmit}
                disabled={loading || !selectedPlan}
              >
                {loading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Processing...
                  </>
                ) : (
                  "Purchase Subscription"
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ViewProfileLayer;
